import { TreatmentPaymentOrder } from '@/components/Timeline/types';
import { useTreatmentPaymentCatalog } from '@/hooks/useTreatmentPaymentCatalog';
import { alert } from '@/services/alert';
import { formatDollars } from '@/services/money';
import { describePaymentError, estimateOrderTotal, findDiscountConflicts } from '@/services/payments';
import { CHECKOUT_SESSION_IDENTIFIER_SYSTEM, writePaymentAttribution } from '@/services/paymentProvenance';
import { fetchBot } from '@/services/services';
import { Alert, Button, Divider, Drawer, Group, MultiSelect, Select, Stack, Text } from '@mantine/core';
import { createReference } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useState } from 'react';

const BOT_NAME = 'create-treatment-payment';

/**
 * The create-order surface. Reached from `Add ▸ Order ▸ Payment order`, which is
 * itself gated on canAccessPayments and ENABLE_TREATMENT_PAYMENTS.
 *
 * Two totals sit here and they are deliberately different things (BAR-2525):
 *
 * - **Estimated total** tracks the form and moves the moment a discount is
 *   picked. It is computed from the catalog, so it is provisional and labelled
 *   as such — the earlier drawer showed no live figure at all, which left a rep
 *   selecting a discount and watching nothing happen.
 * - **Current order** is the server's authoritative arithmetic for the order
 *   that already exists. It answers a different question and is never mixed in
 *   with the estimate.
 *
 * Sending the link is **not** here. On create the drawer closes and the order
 * card on the timeline owns everything afterwards, so success is visible where
 * the order lives rather than as a button quietly appearing in a form.
 *
 * `order` and `onOrderChanged` come from the page's single `usePaymentOrder`
 * instance rather than a second one of our own. Calling the hook here too would
 * refresh only this drawer, leaving the timeline card stale after an order was
 * created — the card and the drawer have to read the same state.
 */
export function PaymentOrderDrawer({
  patient,
  opened,
  onClose,
  order,
  orderError,
  onOrderChanged,
}: {
  patient: Patient;
  opened: boolean;
  onClose: () => void;
  order: TreatmentPaymentOrder | null;
  orderError: string | undefined;
  onOrderChanged: () => Promise<void>;
}) {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { services, discounts, error: catalogError } = useTreatmentPaymentCatalog(opened);

  const [serviceName, setServiceName] = useState<string | null>(null);
  const [discountNames, setDiscountNames] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>();

  async function createOrder() {
    if (!serviceName) {
      return;
    }

    setBusy(true);
    setActionError(undefined);

    try {
      const created = await fetchBot<{ order?: TreatmentPaymentOrder }>(medplum, BOT_NAME, {
        operation: 'createOrder',
        patient: createReference(patient),
        serviceNames: [serviceName],
        discountNames,
      });

      // Who created the order is the win attribution (BAR-2614), and this is
      // the only place it is knowable: bariendo-be authenticates with a shared
      // Bearer key and cannot tell which Orbit user called it, so its
      // `actingUser` is logged and never persisted.
      //
      // Order creation rather than payment because a patient paying their own
      // Stripe link involves no Orbit user at all — there is nobody
      // "completing" the payment to credit.
      //
      // Upsert semantics mean a re-price writes a second Provenance for the
      // same session. That is intended: the reads take the earliest, so the
      // original creator keeps the credit and cannot be taken over by whoever
      // last edited the order.
      // Read defensively rather than destructured: if the bot's response shape
      // ever changes, the cost should be a missing name on a card, not a
      // thrown error telling the PAC an order failed that in fact exists.
      const checkoutSessionId = created?.order?.checkoutSessionId;
      if (checkoutSessionId) {
        await writePaymentAttribution(medplum, {
          system: CHECKOUT_SESSION_IDENTIFIER_SYSTEM,
          value: checkoutSessionId,
          profile,
          component: 'PaymentOrderDrawer',
        });
      }

      // Creating is an upsert server-side: it finds the patient's open order and
      // either re-prices it or returns it unchanged. Re-read rather than assuming
      // a new order was made.
      await onOrderChanged();

      // Cleared before closing: the drawer is not remounted, so a stale
      // selection would still be sitting in the form the next time it opens.
      setServiceName(null);
      setDiscountNames([]);
      alert.success('The payment order has been created.');
      onClose();
    } catch (e) {
      setActionError(describePaymentError(e, 'Could not create the payment order'));
    } finally {
      setBusy(false);
    }
  }

  // Errors render here because this is where someone is trying to act. The
  // timeline card degrades to absent instead of blanking the patient view.
  const error = catalogError ?? orderError ?? actionError;

  // Discounts do not stack — the backend keeps only the largest of each type and
  // silently drops the rest. Shown only once a service is selected, so the
  // warning never appears over an empty form.
  const conflicts = findDiscountConflicts(discountNames, discounts);
  const showConflicts = !!serviceName && (conflicts.ignored.length > 0 || conflicts.tied.length > 0);

  const estimate = estimateOrderTotal(serviceName, discountNames, services, discounts);

  return (
    <Drawer opened={opened} onClose={onClose} position="right" title="New payment order">
      <Stack>
        {error && <Alert color="red">{error}</Alert>}

        <Select
          label="Service"
          placeholder="Select a service"
          data={services.map((service) => ({ value: service.name, label: service.name }))}
          value={serviceName}
          onChange={setServiceName}
        />

        {/* The amount rides alongside the code because the codes are opaque —
            `ESG_500_PROMO` says nothing about what it is worth, and picking
            between two of them was previously guesswork. */}
        <MultiSelect
          label="Discounts"
          placeholder="Optional"
          data={discounts.map((discount) => ({ value: discount.name, label: discount.name }))}
          renderOption={({ option }) => (
            <Group gap="xs" justify="space-between" wrap="nowrap" w="100%">
              {/* The name truncates and the amount does not. Catalog names run
                  long ("DO NOT USE TEMP FIX FOR Wildeline Saint Jean") and the
                  amount is the whole reason this row exists — letting it get
                  squeezed to a bare minus sign is worse than clipping a name. */}
              <Text size="sm" truncate style={{ flex: 1, minWidth: 0 }}>
                {option.label}
              </Text>
              <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                −{formatDollars(discounts.find((discount) => discount.name === option.value)?.amount ?? 0)}
              </Text>
            </Group>
          )}
          value={discountNames}
          onChange={setDiscountNames}
        />

        {showConflicts && (
          <Alert color="yellow" title="Only the largest discount of each kind applies">
            <Stack gap={4}>
              {conflicts.ignored.length > 0 && (
                <Text size="sm">Will not be applied: {conflicts.ignored.join(', ')}.</Text>
              )}
              {conflicts.tied.length > 0 && (
                <Text size="sm">
                  Exactly one of these will be applied — they are the same amount, so which one is not predictable:{' '}
                  {conflicts.tied.join(', ')}.
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        {estimate && (
          <Stack gap={2}>
            <Group justify="space-between">
              <Text fw={600}>Estimated total</Text>
              <Text fw={600}>{formatDollars(estimate.total)}</Text>
            </Group>
            {estimate.discount > 0 && (
              <Text size="sm" c="dimmed">
                Retail {formatDollars(estimate.retail)} · discount −{formatDollars(estimate.discount)}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              Confirmed by the payments system when the order is created.
            </Text>
          </Stack>
        )}

        {order && (
          <>
            <Divider />
            <Stack gap={2}>
              <Text size="xs" fw={600} c="dimmed">
                Current order
              </Text>
              <Text size="sm">
                Total due {formatDollars(order.amountTotal)} · Paid so far {formatDollars(order.amountPaid)} · Balance
                left {formatDollars(order.balance)}
              </Text>
            </Stack>
          </>
        )}

        <Group>
          <Button onClick={createOrder} loading={busy} disabled={!serviceName}>
            Create order
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
